import { FormStateType } from "./Helpers/formTypes";
import { guid } from "./Helpers/guid";
import { LocationDto } from "../../../Api/Model/LocationDto.csharp";
import { formatISO9075 } from "date-fns";

declare var dataLayer: any[] | undefined;

export class GtmService {
  static orderConfirmed(
      formState: FormStateType,
      location: LocationDto | undefined
  ) {
    if (typeof dataLayer != "undefined") {
      const ecommerce = {
        ecommerce: null,
      };

      const data = {
        ecommerce: {
          purchase: {
            actionField: {
              id: guid(),
              affiliation: location?.name ?? formState.location.name,
              revenue: formState.pricingSummary.basePrice,
            },
            products: [
              {
                name: formState.service.name,
                id: "",
                price: formState.pricingSummary.basePrice,
                category: location?.name ?? formState.location.name,
                variant: formState.specialization.name,
                quantity: 1,
              },
            ],
          },
        },
      };
      const event = {
        event: "purchase",
      };

      const category = {
        category: location?.name ?? formState.location.name,
        id: "",
        name: formState.service.name,
        price: formState.pricingSummary.basePrice,
        variant: formState.specialization.name,
      };

      const specializationSelected = {
        event: "specializationSelected",
        specializationName: formState.specialization.name,
      };

      dataLayer.push(ecommerce);
      dataLayer.push(data);
      dataLayer.push(event);
      dataLayer.push(category);
      dataLayer.push(specializationSelected);
    }
  }

  static overlayOpened() {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "bookingOverlayOpened",
      });
    }
  }

  static dropInConfirmed(serviceName: string, centerName: string, id: string) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "WaitingListConfirmed",
        product: serviceName,
        center: centerName,
        uniqueID: id,
      });
    }
  }

  static smsRequested() {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "SMSsent",
      });
    }
  }

  static confirmationError(
      service: string,
      specialization: string,
      center: string,
      message: string
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_error",
        error_type: "booking_confirmation_error",
        stage: "step_3",
        info: {
          name: service,
          category: center,
          variant: specialization,
          message: message,
        },
      });
    }
  }

  static bookingCreationError(
      service: string,
      specialization: string,
      center: string,
      message: string
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_error",
        error_type: "booking_creation_error",
        stage: "step_3",
        info: {
          name: service,
          category: center,
          variant: specialization,
          message: message,
        },
      });
    }
  }

  static appointmentsLoadingError(
      service: string,
      specialization: string,
      center: string,
      message: string
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_error",
        error_type: "appointments_loading_error",
        stage: "step_2",
        info: {
          name: service,
          category: center,
          variant: specialization,
          message: message,
        },
      });
    }
  }

  static genericError(message: string) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_error",
        error_type: "unknown_error",
        stage: "unknown",
        info: {
          message: message,
        },
      });
    }
  }

  static bookingNoOnlineBooking(
      specializationName: string,
      specializationId: string,
      serviceName: string,
      serviceId: string,
      centerName: string,
      centerId: string,
      message: string,
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_error",
        error_type: "No Online Booking",
        stage: "0",
        info: {
          service: specializationName || null,
          service_id: specializationId || null,
          treatment: serviceName || null,
          treatment_id: serviceId || null,
          center: centerName || null,
          center_id: centerId || null,
          message: message || null,
        },
      });
    }
  }

  static bookingOverlayLoaded(
      specializationName?: string,
      specializationId?: string,
      serviceName?: string,
      serviceId?: string,
      centerName?: string,
      centerId?: string,
      doctorName?: string,
      doctorId?: string
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_flow",
        ab_test_variant: "old_booking_flow",
        event_name: "Booking Overlay Opened",
        booking: {
          desc: "booking_overlay_opened",
          tracking_code: "a1",
          info: {
            step: 0,
            service: specializationName || null,
            service_id: specializationId || null,
            treatment: serviceName || null,
            treatment_id: serviceId || null,
            center: centerName || null,
            center_id: centerId || null,
            treatment_provider: doctorName || null,
            doctor_id: doctorId || null,
          },
        },
      });
    }
  }
  
  static bookingSubmitFirstStep(
      specializationName: string,
      specializationId: string,
      serviceName: string,
      serviceId: string,
      centerName: string,
      centerId: string,
      doctorName?: string,
      doctorId?: string
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_flow",
        ab_test_variant: "old_booking_flow",
        event_name: "Specialization, Service and Center Selected",
        booking: {
          desc: "booking_step_one",
          tracking_code: "a2",
          info: {
            step: 1,
            service: specializationName || null,
            service_id: specializationId || null,
            treatment: serviceName || null,
            treatment_id: serviceId || null,
            center: centerName || null,
            center_id: centerId || null,
            treatment_provider: doctorName || null,
            doctor_id: doctorId || null,
          },
        },
      });
    }
  }

  static bookingSubmitSecondStep(
      specializationName: string,
      specializationId: string,
      serviceName: string,
      serviceId: string,
      centerName: string,
      centerId: string,
      doctorName: string,
      doctorId: string,
      appointmentDate: Date
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_flow",
        ab_test_variant: "old_booking_flow",
        event_name: "Appointment Selected",
        booking: {
          desc: "booking_step_two",
          tracking_code: "a3",
          info: {
            step: 2,
            service: specializationName || null,
            service_id: specializationId || null,
            treatment: serviceName || null,
            treatment_id: serviceId || null,
            center: centerName || null,
            center_id: centerId || null,
            treatment_provider: doctorName || null,
            doctor_id: doctorId || null,
            appointment_date: GtmService.formatAppointmentDate(appointmentDate),
            appointment_date_unix_time: appointmentDate.getTime(),
          },
        },
      });
    }
  }

  static bookingSubmitThirdStep(
      specializationName: string,
      specializationId: string,
      serviceName: string,
      serviceId: string,
      centerName: string,
      centerId: string,
      doctorName: string,
      doctorId: string,
      appointmentDate: Date,
      newsletterSignIn: boolean,
      price: number
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_flow",
        ab_test_variant: "old_booking_flow",
        event_name: "Contact Information Submitted",
        booking: {
          desc: "booking_step_three",
          tracking_code: "a4",
          info: {
            step: 3,
            service: specializationName || null,
            service_id: specializationId || null,
            treatment: serviceName || null,
            treatment_id: serviceId || null,
            center: centerName || null,
            center_id: centerId || null,
            treatment_provider: doctorName || null,
            doctor_id: doctorId || null,
            appointment_date: GtmService.formatAppointmentDate(appointmentDate),
            appointment_date_unix_time: appointmentDate.getTime(),
            sms_reminder: true,
            newsletterSignIn: newsletterSignIn,
            price: price,
          },
        },
      });
    }
  }

  static appointmentConfirmed(
      specializationName: string,
      specializationId: string,
      serviceName: string,
      serviceId: string,
      centerName: string,
      centerId: string,
      doctorName: string,
      doctorId: string,
      appointmentDate: Date,
      newsletterSignIn: boolean,
      price: number
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_flow",
        ab_test_variant: "old_booking_flow",
        event_name: "Booking Confirmed",
        booking: {
          desc: "booking_confirmed",
          tracking_code: "a5",
          info: {
            step: 4,
            service: specializationName || null,
            service_id: specializationId || null,
            treatment: serviceName || null,
            treatment_id: serviceId || null,
            center: centerName || null,
            center_id: centerId || null,
            treatment_provider: doctorName || null,
            doctor_id: doctorId || null,
            appointment_date: GtmService.formatAppointmentDate(appointmentDate),
            appointment_date_unix_time: appointmentDate.getTime(),
            sms_reminder: true,
            newsletter_sign_up: newsletterSignIn,
            price: price,
            appointment_id: guid(),
          },
        },
      });
    }
  }

  static bookingAppointmentsScreenShown(
    specializationName: string,
    specializationId: string,
    serviceName: string,
    serviceId: string,
    centerName: string,
    centerId: string,
    doctorName?: string,
    doctorId?: string
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_flow",
        ab_test_variant: "old_booking_flow",
        event_name: "Appointments Screen Shown",
        booking: {
          desc: "appointments_shown",
          tracking_code: "a9",
          info: {
            service: specializationName || null,
            service_id: specializationId || null,
            treatment: serviceName || null,
            treatment_id: serviceId || null,
            center: centerName || null,
            center_id: centerId || null,
            treatment_provider: doctorName || null,
            doctor_id: doctorId || null,
          },
        },
      });
    }
  }
  
  private static formatAppointmentDate(appointmentDate: Date) {
    return formatISO9075(appointmentDate);
  }
}
